<script lang="ts">
import { Auth0PluginApi } from '@/auth'
import HeaderUserMenu from '@/components/header/HeaderUserMenu.vue'
import MainMenu from '@/components/header/MainMenu.vue'
import NotificationsButton from '@/components/header/NotificationsButton.vue'
import SiteDropdown from '@/components/SiteDropdown.vue'
import { useCurrentUser } from '@/composables/useCurrentUser'
import { useCustomersState } from '@/composables/useCustomersState'
import { useMainNavigation } from '@/composables/useMainNavigation'
import { useNotificationState } from '@/composables/useNotificationState'
import { useUserPrivileges } from '@/composables/useUserPrivileges'
import { version } from '@/version'
import { computed, defineComponent, inject, ref } from 'vue'

export default defineComponent({
  name: 'Navigation',
  components: {
    HeaderUserMenu,
    MainMenu,
    NotificationsButton,
    SiteDropdown
  },
  setup() {
    const auth: Auth0PluginApi = inject('auth')

    // Same reason as App.vue: auth.isAuthenticated is an async function, so reading it in
    // the template only ever asks whether a function object exists, which it always does.
    // We resolve it once here and expose the answer as a ref. It stays undefined until
    // Auth0 has answered, and the header controls stay hidden until then, which is the
    // same thing they do for a signed-out visitor.
    const isAuthenticated = ref<boolean | undefined>(undefined)

    auth.isLoaded
      .then(async () => {
        isAuthenticated.value = await auth.isAuthenticated()
      })
      .catch(() => {
        isAuthenticated.value = false
      })

    const { canViewStmNotifications, hasOnlyDsProductAccess, isSuperAdmin } = useUserPrivileges()
    const {
      customers,
      customerSelection,
      totalCustomers,
      totalSelectedCustomers,
      maxSelectionReached,
      maxSelectionExceeded,
      maxCustomerSelectionLimit,
      selectAllCustomers,
      deselectAllCustomers,
      toggleCustomer
    } = useCustomersState()
    const { user } = useCurrentUser()
    const { hasActiveNotifications } = useNotificationState()
    const {
      isItAdmin,
      hasMultipleProductsEnabled,
      currentProduct,
      productOptions,
      selectedProduct,
      routedMenuItems,
      onProductSelected
    } = useMainNavigation()

    const hasAccessToMultipleCustomers = computed(() => totalCustomers.value > 1)
    const selectedCustomerIds = computed(() =>
      Object.keys(customerSelection.value).filter((id) => customerSelection.value[id])
    )

    const username = computed(() => user.value?.username ?? '')

    return {
      isAuthenticated,
      canViewStmNotifications,
      hasAccessToMultipleCustomers,
      hasOnlyDsProductAccess,
      hasActiveNotifications,
      isItAdmin,
      isSuperAdmin,
      hasMultipleProductsEnabled,
      currentProduct,
      productOptions,
      selectedProduct,
      routedMenuItems,
      username,
      version,
      customers,
      selectedCustomerIds,
      totalCustomers,
      totalSelectedCustomers,
      maxSelectionReached,
      maxSelectionExceeded,
      maxCustomerSelectionLimit,
      selectAllCustomers,
      deselectAllCustomers,
      toggleCustomer,
      onProductSelected
    }
  }
})
</script>
<template>
  <header id="topnav" class="header" data-testid="site_header">
    <nav :class="['headerContainer', 'content-container']">
      <div class="header-container-left">
        <router-link class="logoLink" :to="{ name: 'Home' }" aria-label="Home">
          <img
            src="/assets/shoplogix/shoplogix-white.svg"
            alt="Everactive Logo"
            class="desktop-logo logo"
          />
          <img
            src="/assets/shoplogix/shoplogix.svg"
            alt="Everactive Logo"
            class="mobile-logo logo"
          />
        </router-link>
        <SiteDropdown
          v-if="hasAccessToMultipleCustomers && !hasOnlyDsProductAccess"
          :customers="customers"
          :selected-customer-ids="selectedCustomerIds"
          :total-customers="totalCustomers"
          :total-selected-customers="totalSelectedCustomers"
          :max-selection-reached="maxSelectionReached"
          :max-selection-exceeded="maxSelectionExceeded"
          :max-customer-selection-limit="maxCustomerSelectionLimit"
          data-testid="crossSite__dropdown"
          @select-all="selectAllCustomers"
          @deselect-all="deselectAllCustomers"
          @toggle-customer="toggleCustomer"
        />
      </div>
      <div v-if="isAuthenticated" class="navigation">
        <MainMenu
          position="top"
          :current-product="currentProduct"
          :routed-menu-items="routedMenuItems"
          :is-it-admin="isItAdmin"
          :has-multiple-products-enabled="hasMultipleProductsEnabled"
          :product-options="productOptions"
          :selected-product="selectedProduct"
          @update:selected-product="onProductSelected"
        />
        <NotificationsButton
          v-if="canViewStmNotifications"
          :has-active-notifications="hasActiveNotifications"
          data-testid="notificationsButton__button"
        />
        <HeaderUserMenu
          :username="username"
          :is-super-admin="isSuperAdmin"
          :version="version"
          data-testid="headerUserMenu__dropdown"
        />
      </div>
    </nav>
  </header>
</template>

<style scoped>
.header {
  background: #0d4760;
  z-index: 1030;
  height: 68px;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: center;
  box-shadow: 0px -3px 13px 3px rgba(3, 3, 61, 0.5);
}
.headerContainer {
  display: flex;
  justify-content: space-between;
  align-content: center;
  font-family: 'Gilroy', sans-serif;
  font-size: 16px;
}
.header-container-left {
  display: flex;
  align-items: center;
}
.logoLink {
  margin: 10px;
  margin-right: 16px;
}
.logoLink:focus {
  outline-offset: 3px;
}

.logo {
  height: 48px;
  flex: 0 auto;
}

.desktop-logo {
  width: 125px;
}

@media all and (min-width: 425px) {
  .mobile-logo {
    display: none;
  }
}

@media all and (max-width: 425px) {
  .desktop-logo {
    display: none;
  }
}

.navigation {
  display: flex;
  flex: 0 auto;
  align-content: center;
}
</style>
